import React, { useState, useRef, useEffect } from "react";
import { Link as ReactLink, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Form, Button, Container, Row, Col, Spinner } from "react-bootstrap";
import { useAuthStore } from "../model";
import { forgot, verify } from "../api";
import PhoneInput from "react-phone-input-2";
import { getUserByToken } from "@/entities/user/api";
import { PasswordInputGroup, VerificationCodeInput } from "@/shared/ui";

export const ForgotForm = () => {
  const [numberPhone, setNumberPhone] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setUser, setIsAuthorization } = useAuthStore();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showVerification && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [showVerification, timer]);

  const isValidPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, "");
    return (
      digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"))
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidated(true);

    if (!numberPhone || !password) {
      enqueueSnackbar("Пожалуйста, заполните все поля.", { variant: "error" });
      buttonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    
    if (password !== repeatPassword) {
      enqueueSnackbar("Пароли не совпадают.", { variant: "error" });
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (!isValidPhone(numberPhone)) {
      enqueueSnackbar("Введите корректный номер телефона.", {
        variant: "error",
      });
      buttonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    setLoading(true);
    try {
      const digits = numberPhone.replace(/\D/g, "").replace(/^7/, "8");
      const data = await forgot({
        phone: digits,
        password,
      });

      if (data.success) {
        setShowVerification(true);
        setTimer(60);
        enqueueSnackbar("Введите код подтверждения.", { variant: "info" });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent<HTMLFormElement>, code: string) => {
    e.preventDefault();

    if (code.length !== 4) {
      enqueueSnackbar("Введите полный 4-значный код.", { variant: "error" });
      return;
    }

    const digits = numberPhone.replace(/\D/g, "").replace(/^7/, "8");

    try {
      setLoading(true);
      const data = await verify(digits, +code);

      if (data.success) {
        localStorage.setItem("token", data.result);
        setIsAuthorization();
        navigate("/");
        enqueueSnackbar("Смена пароля успешна!", { variant: "success" });

        const me = await getUserByToken(data.result);
        if (me) setUser(me.result);
      } else {
        enqueueSnackbar(data.error || "Неверный код подтверждения", { variant: "error" });
      }
    } catch (err) {
      console.error("Verification error:", err);
      enqueueSnackbar("Ошибка при подтверждении кода", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="max-w-sm mx-auto">
      <Row className="justify-content-md-center mt-5">
        {showVerification ? (
          <Col xs={12} md={5} lg={4}>
            <h2 className="text-center mb-4">Верификация</h2>
            <VerificationCodeInput
              onVerify={handleVerify}
              onBack={() => {
                setShowVerification(false);
                setValidated(false);
              }}
              backTitle="Назад к смене пароля"
              loading={loading}
              timer={timer}
              onResend={() => setTimer(60)}
            />
          </Col>
        ) : (
          <Col xs={12} md={5} lg={4}>
            <h2 className="text-center mb-4">Забыл пароль</h2>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <Form.Group controlId="formPhone" className="mb-3">
                <Form.Label>Номер телефона</Form.Label>

                <PhoneInput
                  country={"ru"}
                  disableDropdown={true}
                  specialLabel=""
                  masks={{ ru: "(...) ...-..-.." }}
                  onlyCountries={["ru"]}
                  placeholder="Введите номер телефона"
                  value={numberPhone}
                  onChange={(phone) => {
                    if (phone.startsWith("8")) {
                      setNumberPhone(`7${phone.slice(1)}`);
                    } else {
                      setNumberPhone(phone);
                    }
                  }}
                  inputClass="form-control rounded-5"
                />

                <Form.Control.Feedback type="invalid">
                  Введите корректный номер телефона.
                </Form.Control.Feedback>
              </Form.Group>

              <PasswordInputGroup
                label="Новый пароль"
                value={password}
                name="password"
                onChange={(e) => setPassword(e.target.value)}
                showPassword={showPassword}
                toggleShow={() => setShowPassword(!showPassword)}
                placeholder="Введите новый пароль"
                isInvalid={validated && !password}
                feedback="Пожалуйста, введите пароль."
              />

                       <PasswordInputGroup
                            label="Повторите пароль"
                            value={repeatPassword}
                            name="repeatPassword"
                            onChange={(e) => setRepeatPassword(e.target.value)}
                            showPassword={showRepeatPassword}
                            toggleShow={() => setShowRepeatPassword(!showRepeatPassword)}
                            placeholder="Повторите новый пароль"
                            isInvalid={validated && (repeatPassword !== password || !repeatPassword)}
                            feedback="Пароли не совпадают"
                          />

              {/* <Form.Group controlId="formPassword" className="mb-3">
                <Form.Label>Новый пароль</Form.Label>
                <Form.Control
                  required
                  type="password"
                  placeholder="Введите новый пароль"
                  className="rounded-5"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isInvalid={validated && !password}
                />
              </Form.Group> */}

              <Button
                ref={buttonRef}
                variant="outline-secondary"
                type="submit"
                className="w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Отправить...
                  </>
                ) : (
                  "Отправить"
                )}
              </Button>
            </Form>
            <div className="text-center mt-3">
              <ReactLink to="/login" className="text-secondary">
                Авторизация
              </ReactLink>
            </div>
          </Col>
        )}
      </Row>
    </Container>
  );
};
